import React, { useState } from 'react';
import {
  Utensils,
  Car,
  Zap,
  Gamepad2,
  ShoppingBag,
  Heart,
  GraduationCap,
  PiggyBank,
  MoreHorizontal,
  Check,
  Plus,
  X,
  Home,
  Plane,
  Music,
  Dumbbell,
  Dog,
  Gift,
  Coffee,
  Wifi,
  Phone,
  Shirt,
  Baby,
  Briefcase,
  Book,
  Camera,
  Tv,
  Palette,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBudget, CATEGORIES } from '@/contexts/BudgetContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const iconMap = {
  Utensils,
  Car,
  Zap,
  Gamepad2,
  ShoppingBag,
  Heart,
  GraduationCap,
  PiggyBank,
  MoreHorizontal,
  Home,
  Plane,
  Music,
  Dumbbell,
  Dog,
  Gift,
  Coffee,
  Wifi,
  Phone,
  Shirt,
  Baby,
  Briefcase,
  Book,
  Camera,
  Tv,
  Palette,
};

// Available icons for custom categories
const availableIcons = [
  { name: 'Home', icon: Home, color: '#6366f1' },
  { name: 'Plane', icon: Plane, color: '#0ea5e9' },
  { name: 'Music', icon: Music, color: '#8b5cf6' },
  { name: 'Dumbbell', icon: Dumbbell, color: '#ef4444' },
  { name: 'Dog', icon: Dog, color: '#f97316' },
  { name: 'Gift', icon: Gift, color: '#ec4899' },
  { name: 'Coffee', icon: Coffee, color: '#78716c' },
  { name: 'Wifi', icon: Wifi, color: '#14b8a6' },
  { name: 'Phone', icon: Phone, color: '#3b82f6' },
  { name: 'Shirt', icon: Shirt, color: '#a855f7' },
  { name: 'Baby', icon: Baby, color: '#f472b6' },
  { name: 'Briefcase', icon: Briefcase, color: '#64748b' },
  { name: 'Book', icon: Book, color: '#22c55e' },
  { name: 'Camera', icon: Camera, color: '#eab308' },
  { name: 'Tv', icon: Tv, color: '#6366f1' },
  { name: 'Palette', icon: Palette, color: '#f43f5e' },
  { name: 'Car', icon: Car, color: '#3b82f6' },
  { name: 'Heart', icon: Heart, color: '#ef4444' },
  { name: 'ShoppingBag', icon: ShoppingBag, color: '#ec4899' },
  { name: 'MoreHorizontal', icon: MoreHorizontal, color: '#6b7280' },
];

// Predefined categories that cannot be deleted
const PREDEFINED_CATEGORIES = new Set([
  "Food & Dining", "Transportation", "Utilities", "Entertainment",
  "Shopping", "Healthcare", "Education", "Savings", "Other"
]);

const BudgetGoalItem = ({ category, config, currentGoal, onUpdate, onDelete, currencySymbol, customMeta }) => {
  const [value, setValue] = useState(currentGoal.toString());
  const [isEditing, setIsEditing] = useState(false);
  
  const isCustomCategory = !PREDEFINED_CATEGORIES.has(category);
  
  // Check for custom icon first (from customMeta), then config icon, then default
  const customIcon = customMeta;
  const IconComponent = customIcon?.icon ? (iconMap[customIcon.icon] || MoreHorizontal) : (iconMap[config?.icon] || MoreHorizontal);
  const iconColor = customIcon?.color || (config?.color ? `hsl(var(--${config.color}))` : '#6b7280');

  const handleSave = () => {
    const newValue = parseFloat(value) || 0;
    onUpdate(category, newValue);
    setIsEditing(false);
    toast.success(`${category} budget updated to ${currencySymbol}${newValue}`);
  };

  const handleDelete = () => {
    if (confirm(`Delete "${category}" category? This cannot be undone.`)) {
      onDelete(category);
    }
  };

  return (
    <div className="flex items-center gap-3 py-3 group">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
        style={{
          backgroundColor: customIcon?.color ? `${customIcon.color}15` : `hsl(var(--${config?.color || 'muted-foreground'}) / 0.15)`,
        }}
      >
        <IconComponent
          className="w-5 h-5"
          style={{ color: iconColor }}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{category}</p>
        {isCustomCategory && (
          <p className="text-xs text-muted-foreground">Custom</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <div className="relative w-24">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {currencySymbol}
              </span>
              <Input
                type="number"
                min="0"
                step="10"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="h-8 pl-5 pr-2 text-sm"
                autoFocus
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-success"
              onClick={handleSave}
            >
              <Check className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              className="h-8 px-3 text-sm font-medium"
              onClick={() => setIsEditing(true)}
            >
              {currencySymbol}{currentGoal.toLocaleString()}
            </Button>
            {isCustomCategory && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={handleDelete}
                title="Delete category"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const BudgetGoalsSettings = () => {
  const { budgetGoals, updateBudgetGoal, deleteCustomCategory, getUniqueCategories, currencySymbol, convertToSelectedCurrency, settings, customCategoriesMeta } = useBudget();
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryBudget, setNewCategoryBudget] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(availableIcons[0]);

  // Get all categories (default + custom from transactions + custom budget goals)
  const uniqueCats = getUniqueCategories();
  const allCategories = new Set([
    ...Object.keys(CATEGORIES),
    ...uniqueCats.expense,
    ...Object.keys(budgetGoals)
  ]);
  
  const categoryList = Array.from(allCategories);

  // Convert budget goals to selected currency for display
  const convertedBudgetGoals = {};
  for (const [cat, amount] of Object.entries(budgetGoals)) {
    convertedBudgetGoals[cat] = convertToSelectedCurrency(amount, 'CAD');
  }

  const totalBudget = Object.values(convertedBudgetGoals).reduce((sum, val) => sum + val, 0);

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) {
      toast.error('Please enter a category name');
      return;
    }
    if (allCategories.has(name)) {
      toast.error('Category already exists');
      return;
    }
    const budget = parseFloat(newCategoryBudget) || 0;
    
    // Pass full metadata for custom category
    updateBudgetGoal(name, {
      budget,
      icon: selectedIcon.name,
      color: selectedIcon.color
    });
    
    toast.success(`Added "${name}" category with ${currencySymbol}${budget} budget`);
    setNewCategoryName('');
    setNewCategoryBudget('');
    setSelectedIcon(availableIcons[0]);
    setShowAddCategory(false);
  };

  const handleDeleteCategory = async (category) => {
    const success = await deleteCustomCategory(category);
    if (success) {
      toast.success(`Deleted "${category}" category`);
    } else {
      toast.error(`Failed to delete "${category}"`);
    }
  };

  // Build category items
  const categoryItems = [];
  for (let i = 0; i < categoryList.length; i++) {
    const category = categoryList[i];
    const config = CATEGORIES[category] || { icon: 'MoreHorizontal', color: 'muted-foreground' };
    categoryItems.push(
      <BudgetGoalItem
        key={category}
        category={category}
        config={config}
        currentGoal={convertedBudgetGoals[category] || 0}
        onUpdate={updateBudgetGoal}
        onDelete={handleDeleteCategory}
        currencySymbol={currencySymbol}
        customMeta={customCategoriesMeta[category]}
      />
    );
  }

  // Build icon picker options
  const iconOptions = [];
  for (let i = 0; i < availableIcons.length; i++) {
    const iconData = availableIcons[i];
    const IconComp = iconData.icon;
    iconOptions.push(
      <button
        key={iconData.name}
        onClick={() => setSelectedIcon(iconData)}
        className={cn(
          "w-10 h-10 rounded-xl flex items-center justify-center transition-all",
          selectedIcon.name === iconData.name 
            ? "ring-2 ring-primary ring-offset-2 ring-offset-background" 
            : "hover:bg-muted"
        )}
        style={{ backgroundColor: `${iconData.color}15` }}
      >
        <IconComp className="w-5 h-5" style={{ color: iconData.color }} />
      </button>
    );
  }

  const SelectedIconComp = selectedIcon.icon;

  return (
    <Card className="border border-border/50 shadow-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Budget Goals</CardTitle>
            <CardDescription className="text-xs mt-1">
              Set monthly spending limits for each category
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Total Budget</p>
            <p className="text-lg font-bold text-foreground">
              {currencySymbol}{totalBudget.toLocaleString()}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border/50">
          {categoryItems}
        </div>
        
        {/* Add Category Section */}
        {showAddCategory ? (
          <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border/50">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Add New Category</span>
            </div>
            
            {/* Icon Picker */}
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-2">Choose an icon</p>
              <div className="flex flex-wrap gap-2">
                {iconOptions}
              </div>
            </div>
            
            {/* Preview and inputs */}
            <div className="flex items-center gap-3 mb-3 p-3 bg-background rounded-lg border border-border/50">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${selectedIcon.color}15` }}
              >
                <SelectedIconComp className="w-5 h-5" style={{ color: selectedIcon.color }} />
              </div>
              <Input
                placeholder="Category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="flex-1 h-9 border-0 bg-transparent focus-visible:ring-0 px-0"
              />
              <div className="relative w-20">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{currencySymbol}</span>
                <Input
                  type="number"
                  placeholder="0"
                  value={newCategoryBudget}
                  onChange={(e) => setNewCategoryBudget(e.target.value)}
                  className="h-9 pl-5 text-right"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddCategory} className="flex-1">
                <Check className="w-4 h-4 mr-1" /> Add Category
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAddCategory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <Button 
            variant="outline" 
            className="w-full mt-4 border-dashed"
            onClick={() => setShowAddCategory(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Category
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
